import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { CoverPage } from '../components/CoverPage';
import DossierContentEditor from '../components/DossierContentEditor';
import MatchingTendersModal from '../components/MatchingTendersModal';
import {
  getTheme, DEFAULT_TEMPLATE, PAGE_W,
  normalizeCompetences, normalizeFormations, normalizeLanguages, normalizeExperiences,
} from '../lib/dossierThemes';
import { dossiersAPI, orgsAPI, profileAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { applyColorOverride } from '../lib/colorPalettes';
import ColorPaletteSelector from '../components/ColorPaletteSelector';
import { Download, Save, ArrowLeft, Loader2, Target, Palette, Eye, EyeOff } from 'lucide-react';

const DossierEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentOrg } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dossierName, setDossierName] = useState('');

  // Structured content
  const [formations, setFormations] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [competences, setCompetences] = useState([]);
  const [experiences, setExperiences] = useState([]);

  // Cover / meta
  const [coverName, setCoverName] = useState('');
  const [coverTitle, setCoverTitle] = useState('');
  const [coverEmail, setCoverEmail] = useState('');
  const [availability, setAvailability] = useState('');
  const [coverTrigram, setCoverTrigram] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoScale, setLogoScale] = useState(1);
  const [showFullName, setShowFullName] = useState(false);
  const [colorOverride, setColorOverride] = useState(null);
  const [colorsOpen, setColorsOpen] = useState(false);
  const [matchingTendersOpen, setMatchingTendersOpen] = useState(false);

  // Refs for autosave snapshots (avoid stale closures)
  const r = useRef({});
  const saveTimer = useRef(null);
  useEffect(() => {
    r.current = {
      formations, languages, competences, experiences,
      coverName, coverTitle, coverEmail, availability, coverTrigram,
      logoScale, showFullName, colorOverride,
    };
  });

  const template = DEFAULT_TEMPLATE;

  useEffect(() => {
    document.title = 'Skilldeck — Éditeur';
    dossiersAPI.getOne(id)
      .then((d) => {
        setDossierName(d.name || d.consultant_name || '');
        setFormations(normalizeFormations(d.formations));
        setLanguages(normalizeLanguages(d.languages));
        setCompetences(normalizeCompetences(d.competences));
        setExperiences(normalizeExperiences(d.experiences));
        setCoverName(d.consultant_name || '');
        setCoverTitle(d.consultant_title || '');
        setColorOverride(d.color_override || null);
        const ov = d.cover_overrides || {};
        setLogoScale(typeof ov.logo_scale === 'number' ? ov.logo_scale : 1);
        setShowFullName(!!ov.show_full_name);
        setAvailability(ov.availability || '');
        setCoverTrigram(ov.trigram || '');
        setCoverEmail(ov.contact_email || d.consultant_email || '');
      })
      .catch(() => toast({ title: 'Erreur', description: 'Impossible de charger le dossier.', variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    const orgId = localStorage.getItem('currentOrgId');
    if (orgId) orgsAPI.getLogo(orgId).then((res) => setLogoUrl(res?.logo_url || null)).catch(() => {});
    // Default the contact email to the current user's email when none is set yet.
    profileAPI.get().then((p) => {
      setCoverEmail((cur) => cur || p?.email || '');
    }).catch(() => {});
  }, []);

  // Expose a live snapshot for the bug-report attachment.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.__skilldeck_dossier_draft = () => ({ dossier_id: id, ...r.current });
    return () => { delete window.__skilldeck_dossier_draft; };
  }, [id]);

  const performSave = useCallback(async (silent = false) => {
    const s = r.current;
    if (!silent) setSaving(true);
    try {
      await dossiersAPI.update(id, {
        consultant_name: s.coverName,
        consultant_title: s.coverTitle,
        competences: s.competences,
        formations: s.formations,
        experiences: s.experiences,
        languages: s.languages,
      });
      await dossiersAPI.saveEditorContent(id, {
        template_id: template,
        color_override: s.colorOverride || {},
        cover_overrides: {
          logo_scale: s.logoScale,
          show_full_name: s.showFullName,
          availability: s.availability,
          contact_email: s.coverEmail,
          trigram: s.coverTrigram,
        },
      });
      if (!silent) toast({ title: 'Sauvegardé', description: 'Modifications enregistrées.' });
    } catch {
      if (!silent) toast({ title: 'Erreur', description: 'Impossible de sauvegarder.', variant: 'destructive' });
    } finally {
      if (!silent) setSaving(false);
    }
  }, [id, template]);

  const scheduleAutosave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => performSave(true), 1500);
  }, [performSave]);

  // Wrap a state setter so any edit triggers a debounced autosave.
  const tracked = (setter) => (value) => {
    setter(value);
    scheduleAutosave();
  };

  const handleColorOverrideChange = useCallback((next) => {
    const value = next && (next.primary || next.accent) ? next : null;
    setColorOverride(value);
    scheduleAutosave();
  }, [scheduleAutosave]);

  const handleToggleFullName = useCallback(() => {
    setShowFullName((p) => !p);
    scheduleAutosave();
  }, [scheduleAutosave]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await performSave(true);
      await dossiersAPI.downloadPDF(id, r.current.coverName || 'dossier');
    } catch {
      toast({ title: 'Erreur', description: "L'export PDF a échoué.", variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const theme = applyColorOverride(getTheme(template), colorOverride);

  if (loading) {
    return (
      <AppLayout title="Éditeur">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, gap: 10, color: '#6080a0' }}>
          <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /> Chargement du dossier…
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Éditeur de dossier">
      {/* ── Action bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px', background: '#fff', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 30, flexWrap: 'wrap', gap: 8 }}>
        <button onClick={() => navigate('/dashboard')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#6080a0', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
          <ArrowLeft size={16} /> Tableau de bord
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1A1240' }}>{dossierName || coverName || 'Dossier sans titre'}</span>

          {/* Colors */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setColorsOpen((o) => !o)} title="Personnaliser les couleurs"
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px 6px 6px', borderRadius: 20, border: `1.5px solid ${colorOverride ? theme.accent : '#e5e7eb'}`, background: '#fff', color: colorOverride ? theme.accent : '#6b7280', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              <span style={{ width: 22, height: 22, borderRadius: 14, background: `linear-gradient(135deg, ${(colorOverride?.primary) || theme.ink} 50%, ${(colorOverride?.accent) || theme.accent} 50%)`, border: '1px solid #e5e7eb', display: 'inline-block' }} />
              <span>Couleurs</span><Palette size={13} style={{ opacity: 0.7 }} />
            </button>
            {colorsOpen && (
              <>
                <div onClick={() => setColorsOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                <div style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 41, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,0.15)', padding: 14, width: 360 }}>
                  <ColorPaletteSelector value={colorOverride} onChange={handleColorOverrideChange} onClear={() => handleColorOverrideChange(null)} />
                </div>
              </>
            )}
          </div>

          {/* Full name toggle */}
          <button onClick={handleToggleFullName} title={showFullName ? 'Masquer le nom complet' : 'Afficher le nom complet'}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 12px', borderRadius: 20, border: `1.5px solid ${showFullName ? theme.accent : '#e5e7eb'}`, background: '#fff', color: showFullName ? theme.accent : '#6b7280', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {showFullName ? <Eye size={14} /> : <EyeOff size={14} />}<span>Nom complet</span>
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setMatchingTendersOpen(true)} title="Voir les AO ouverts qui matchent ce consultant"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', color: '#6080a0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <Target size={14} /> Matching AO
          </button>
          <button onClick={() => performSave(false)} disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}{saving ? 'Sauvegarde…' : 'Sauvegarder'}
          </button>
          <button onClick={handleExport} disabled={exporting}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 7, border: 'none', background: theme.ink, color: '#fff', fontSize: 13, fontWeight: 600, cursor: exporting ? 'not-allowed' : 'pointer', opacity: exporting ? 0.7 : 1 }}>
            {exporting ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={14} />}{exporting ? 'Export…' : 'Exporter PDF'}
          </button>
        </div>
      </div>

      {/* ── A4 canvas ── */}
      <div style={{ background: '#e5e7eb', minHeight: 'calc(100vh - 60px)', padding: '32px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
        <div style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.10)', background: '#fff' }}>
          <CoverPage
            name={coverName}
            title={coverTitle}
            setTitle={tracked(setCoverTitle)}
            theme={theme}
            logoUrl={logoUrl}
            editable
            orgId={currentOrg?.id || localStorage.getItem('currentOrgId')}
            onLogoChange={setLogoUrl}
            logoScale={logoScale}
            setLogoScale={tracked(setLogoScale)}
            showFullName={showFullName}
            email={coverEmail}
            setEmail={tracked(setCoverEmail)}
            availability={availability}
            setAvailability={tracked(setAvailability)}
            trigram={coverTrigram}
            setTrigram={tracked(setCoverTrigram)}
            brand={currentOrg?.name || ''}
          />
        </div>

        <div style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.10)', background: '#fff', width: PAGE_W }}>
          <DossierContentEditor
            theme={theme}
            formations={formations} setFormations={tracked(setFormations)}
            languages={languages} setLanguages={tracked(setLanguages)}
            competences={competences} setCompetences={tracked(setCompetences)}
            experiences={experiences} setExperiences={tracked(setExperiences)}
          />
        </div>
      </div>

      <MatchingTendersModal open={matchingTendersOpen} dossierId={id} onClose={() => setMatchingTendersOpen(false)} />

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </AppLayout>
  );
};

export default DossierEditor;
